import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, Bell, Calendar, Pin, ChevronDown, ChevronUp,
  Megaphone, AlertCircle, Info, CheckCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import Navigation from '@/components/Navigation';
import { useTheme } from '@/contexts/ThemeContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PRIORITY_CONFIG = {
  urgent: { color: 'bg-red-500', icon: AlertCircle, label: 'Urgent' },
  important: { color: 'bg-orange-500', icon: Megaphone, label: 'Important' },
  normal: { color: 'bg-blue-500', icon: Info, label: 'Info' }
};

const AnnouncementsPage = ({ user }) => {
  const navigate = useNavigate();
  const { colors } = useTheme();
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedIds, setExpandedIds] = useState({});

  useEffect(() => {
    fetchAnnouncements();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAnnouncements = async () => {
    try {
      const res = await fetch(`${API_URL}/api/announcements/history`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.ok) {
        setAnnouncements(await res.json());
      }
    } catch (error) {
      console.error('Error fetching announcements:', error);
    }
    setLoading(false);
  };

  const toggleExpand = (id) => {
    setExpandedIds(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const getBackRoute = () => {
    switch (user.role) {
      case 'youth': return '/dashboard';
      case 'parent': return '/parent';
      case 'lead_monitor': return '/lead-monitor';
      case 'platform_admin': return '/platform-admin';
      default: return '/dashboard';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-2xl font-heading font-bold text-primary">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-24" style={{ backgroundColor: colors?.background || '#FEF9F3' }}>
      {/* Header */}
      <div className="p-6 rounded-b-3xl" style={{ background: colors?.header_gradient || colors?.header || '#1B2D4F', color: '#fff' }}>
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-4 mb-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(getBackRoute())}
              className="text-white hover:bg-white/20 rounded-full"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-6 h-6" />
            </Button>
            <div className="flex items-center gap-3">
              <Megaphone className="w-10 h-10" />
              <div>
                <h1 className="font-heading text-3xl font-bold" style={{ color: "white" }}>Announcements</h1>
                <p style={{ color: "rgba(255,255,255,0.9)" }}>Important updates from program staff</p>
              </div>
            </div>
          </div>

          {/* Stats */}
          <div className="bg-white/10 backdrop-blur rounded-xl p-4 flex items-center justify-around">
            <div className="text-center">
              <p className="text-2xl font-bold">{announcements.length}</p>
              <p className="text-xs opacity-75">Total</p>
            </div>
            <div className="h-8 w-px bg-white/30" />
            <div className="text-center">
              <p className="text-2xl font-bold">
                {announcements.filter(a => a.priority === 'urgent').length}
              </p>
              <p className="text-xs opacity-75">Urgent</p>
            </div>
            <div className="h-8 w-px bg-white/30" />
            <div className="text-center">
              <p className="text-2xl font-bold">
                {announcements.filter(a => a.priority === 'important').length}
              </p>
              <p className="text-xs opacity-75">Important</p>
            </div>
          </div>
        </div>
      </div>

      {/* Announcements List */}
      <div className="max-w-4xl mx-auto px-4 py-6 space-y-4">
        {announcements.length === 0 ? (
          <Card className="p-8 text-center rounded-3xl">
            <Megaphone className="w-16 h-16 text-indigo-300 mx-auto mb-4" />
            <h2 className="font-heading text-xl font-bold mb-2">No Announcements</h2>
            <p className="text-muted-foreground">
              There are no announcements at this time. Check back later!
            </p>
          </Card>
        ) : (
          announcements.map((announcement, index) => {
            const config = PRIORITY_CONFIG[announcement.priority] || PRIORITY_CONFIG.normal;
            const PriorityIcon = config.icon;
            const isExpanded = expandedIds[announcement.id];
            
            return (
              <motion.div
                key={announcement.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <Card 
                  className={`rounded-2xl overflow-hidden ${
                    announcement.priority === 'urgent' ? 'border-2 border-red-300' : ''
                  }`}
                >
                  <div 
                    className="p-4 cursor-pointer"
                    onClick={() => toggleExpand(announcement.id)}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-10 h-10 rounded-xl ${config.color} flex items-center justify-center flex-shrink-0`}>
                        <PriorityIcon className="w-5 h-5 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <Badge className={config.color}>{config.label}</Badge>
                          {announcement.is_read && (
                            <Badge variant="outline" className="text-xs text-green-600">
                              <CheckCircle className="w-3 h-3 mr-1" /> Read
                            </Badge>
                          )}
                        </div>
                        <h3 className="font-semibold text-gray-900">{announcement.title}</h3>
                        <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {new Date(announcement.created_at).toLocaleDateString('en-US', {
                            weekday: 'short', month: 'short', day: 'numeric', year: 'numeric'
                          })}
                          {announcement.created_by_name && (
                            <span> • by {announcement.created_by_name}</span>
                          )}
                        </p>
                      </div>
                      {isExpanded ? (
                        <ChevronUp className="w-5 h-5 text-gray-400" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-gray-400" />
                      )}
                    </div>
                  </div>

                  {/* Expanded Content */}
                  {isExpanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      className="border-t px-4 pb-4"
                    >
                      <div className="pt-4">
                        <p className="text-gray-700 whitespace-pre-wrap">{announcement.content}</p>
                        {announcement.action_url && (
                          <Button
                            variant="outline"
                            className="mt-4 rounded-full"
                            onClick={() => window.open(announcement.action_url, '_blank')}
                          >
                            {announcement.action_text || 'Learn More'}
                          </Button>
                        )}
                      </div>
                    </motion.div>
                  )}
                </Card>
              </motion.div>
            );
          })
        )}
      </div>

      <Navigation role={user.role} />
    </div>
  );
};

export default AnnouncementsPage;
